import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Instagram, Facebook, Linkedin, Home, User, Layout, Briefcase, Mail, Shield, Phone, ArrowRight, CheckCircle2, Zap, Target, ArrowUp } from "lucide-react";
import { FaWhatsapp, FaBehance } from "react-icons/fa";
import logoMonk from "@/assets/mkstd-branca.png";

const Footer = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="bg-[#0A0A0A] pt-20 pb-10 border-t border-white/5 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-primary/5 blur-[120px] rounded-full pointer-events-none opacity-50" />
      <div className="absolute bottom-0 left-0 w-[300px] h-[300px] bg-orange-500/5 blur-[100px] rounded-full pointer-events-none opacity-30" />

      <div className="max-w-[1280px] mx-auto px-6 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-12 lg:gap-8 mb-16">
          
          {/* Col 1: Logo & Info (Lg: 4 cols) */}
          <div className="lg:col-span-4 flex flex-col items-start">
            <Link to="/" className="mb-6 group">
              <img src={logoMonk} alt="Monk Studio" className="h-10 w-auto transition-transform duration-300 group-hover:scale-105" />
            </Link>
            
            <div className="h-0.5 w-12 bg-gradient-to-r from-[#FFB800] to-[#FF5C7A] mb-8" />
            
            <p className="text-white/60 text-[15px] leading-relaxed mb-10 max-w-xs">
              Estratégia, design e tecnologia para transformar visitantes em clientes.
            </p>

            <div className="space-y-4">
              <h4 className="text-white font-bold text-sm tracking-wide">Siga a Monk</h4>
              <div className="flex gap-3">
                {[
                  { icon: Instagram, url: "https://instagram.com/monkstd", color: "hover:bg-pink-600/20 hover:text-pink-500" },
                  { icon: Facebook, url: "https://facebook.com/monkstd", color: "hover:bg-blue-600/20 hover:text-blue-500" },
                  { icon: Linkedin, url: "https://linkedin.com/company/monkstd", color: "hover:bg-blue-700/20 hover:text-blue-400" },
                  { icon: FaBehance, url: "https://www.behance.net/monkstd", color: "hover:bg-blue-500/20 hover:text-blue-300" }
                ].map((social, idx) => (
                  <a 
                    key={idx}
                    href={social.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className={`w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center text-white/40 transition-all duration-300 ${social.color} border border-white/5 hover:border-white/10`}
                  >
                    <social.icon className="w-5 h-5" />
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Col 2: Navegue (Lg: 2 cols) */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-2 mb-8">
              <div className="w-2 h-2 rounded-full bg-[#FFB800]" />
              <h3 className="text-white font-bold text-sm uppercase tracking-widest">Navegue</h3>
            </div>
            <div className="flex flex-col gap-4">
              {[
                { label: "Início", path: "/", icon: Home },
                { label: "Sobre", path: "/sobre", icon: User },
                { label: "Soluções", path: "/solucoes", icon: Layout },
                { label: "Portfólio", path: "/portfolio", icon: Briefcase },
                { label: "Contato", path: "/contato", icon: Mail },
                { label: "Política de Privacidade", path: "/privacidade", icon: Shield }
              ].map((link, idx) => (
                <Link 
                  key={idx}
                  to={link.path} 
                  className="group flex items-center gap-3 text-white/50 hover:text-white transition-all text-sm font-medium"
                >
                  <link.icon className="w-4 h-4 text-white/20 group-hover:text-[#FFB800] transition-colors" />
                  {link.label}
                </Link>
              ))}
            </div>
          </div>

          {/* Col 3: Contato (Lg: 3 cols) */}
          <div className="lg:col-span-3">
            <div className="flex items-center gap-2 mb-8">
              <div className="w-2 h-2 rounded-full bg-[#FFB800]" />
              <h3 className="text-white font-bold text-sm uppercase tracking-widest">Fale com a Monk</h3>
            </div>
            <div className="space-y-4 mb-8">
              <a href="tel:+5521977435758" className="flex items-center gap-4 p-4 rounded-2xl bg-white/5 border border-white/5 hover:border-white/10 transition-all group">
                <div className="w-10 h-10 rounded-xl bg-[#FFB800]/10 flex items-center justify-center text-[#FFB800] group-hover:bg-[#FFB800]/20 transition-colors">
                  <Phone className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-white font-bold text-sm">(21) 97743-5758</p>
                  <p className="text-white/40 text-xs">WhatsApp</p>
                </div>
              </a>
              <a href="mailto:contato@monkstudio.com.br" className="flex items-center gap-4 p-4 rounded-2xl bg-white/5 border border-white/5 hover:border-white/10 transition-all group">
                <div className="w-10 h-10 rounded-xl bg-[#FFB800]/10 flex items-center justify-center text-[#FFB800] group-hover:bg-[#FFB800]/20 transition-colors flex-shrink-0">
                  <Mail className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-white font-bold text-sm">contato@monkstudio.com.br</p>
                  <p className="text-white/40 text-xs">E-mail</p>
                </div>
              </a>
            </div>

            <a 
              href="https://wa.me/5521977435758" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center justify-between gap-4 p-5 rounded-2xl bg-gradient-to-r from-[#FFB800]/20 to-[#FF5C7A]/20 border border-[#FFB800]/30 hover:border-[#FFB800]/60 transition-all group relative overflow-hidden"
            >
              <div className="flex items-center gap-4 relative z-10">
                <div className="w-12 h-12 rounded-full bg-gradient-to-r from-[#FFB800] to-[#FF7A00] flex items-center justify-center text-white shadow-lg shadow-orange-500/20 group-hover:scale-110 transition-transform">
                  <FaWhatsapp className="w-6 h-6" />
                </div>
                <p className="text-white font-bold text-sm leading-tight">Vamos conversar<br />sobre seu projeto?</p>
              </div>
              <ArrowRight className="w-5 h-5 text-white/40 group-hover:text-white group-hover:translate-x-1 transition-all relative z-10" />
              <div className="absolute inset-0 bg-gradient-to-r from-orange-500/10 to-pink-500/10 opacity-0 group-hover:opacity-100 transition-opacity" />
            </a>
          </div>

          {/* Col 4: Action (Lg: 3 cols) */}
          <div className="lg:col-span-3">
            <div className="relative p-8 rounded-3xl bg-white/5 border border-white/5 overflow-hidden h-full flex flex-col justify-center">
              <div className="absolute top-4 left-4">
                <Target className="w-8 h-8 text-[#FFB800] opacity-50" />
              </div>
              <div className="relative z-10">
                <h4 className="text-white font-bold text-lg mb-4 leading-tight">
                  Criamos experiências que geram <span className="text-[#FFB800]">resultados reais</span> para o seu negócio.
                </h4>
                <div className="h-1 w-12 bg-[#FFB800] rounded-full mb-6" />
                <p className="text-white/40 text-xs leading-relaxed uppercase tracking-widest font-bold">
                  Landing pages, tráfego pago, design e automações.
                </p>
              </div>
              
              {/* Decorative sphere */}
              <div className="absolute -bottom-20 -right-20 w-40 h-40 bg-white/5 rounded-full blur-3xl pointer-events-none" />
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-white/5 pt-10 flex flex-col lg:flex-row items-center justify-between gap-8">
          <div className="flex flex-col items-center lg:items-start">
            <p className="text-white/60 text-sm font-bold mb-1">© 2011 Monk Studio.</p>
            <p className="text-white/20 text-xs font-medium uppercase tracking-widest">Todos os direitos reservados.</p>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-8 text-white/40 text-xs font-bold uppercase tracking-widest">
            <div className="flex items-center gap-2 group transition-colors hover:text-white">
              <CheckCircle2 className="w-4 h-4 text-[#FFB800]" />
              <span>Foco em performance</span>
            </div>
            <div className="hidden sm:block w-px h-4 bg-white/10" />
            <div className="flex items-center gap-2 group transition-colors hover:text-white">
              <Zap className="w-4 h-4 text-[#FFB800]" />
              <span>Tecnologia e estratégia</span>
            </div>
            <div className="hidden sm:block w-px h-4 bg-white/10" />
            <div className="flex items-center gap-2 group transition-colors hover:text-white">
              <Target className="w-4 h-4 text-[#FFB800]" />
              <span>Resultados que importam</span>
            </div>
          </div>

          <button 
            onClick={scrollToTop}
            className="w-12 h-12 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center text-white/40 hover:text-white hover:bg-white/10 hover:border-white/20 transition-all group"
            aria-label="Voltar ao topo"
          >
            <ArrowUp className="w-5 h-5 group-hover:-translate-y-1 transition-transform" />
          </button>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
